import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilter,
  faStar,
  faEnvelope,
  faCaretDown,
  faCaretUp,
  faMap,
  faList,
  faCircleXmark,
  faShieldAlt,
  faCheckCircle,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";
import "./components/find-property.css";
import { getPropertyImageUrl } from "../../../utils/property-image";
import { useLanguage } from "../../../i18n/LanguageContext";
import PropertyMapView from "./components/PropertyMapView";

const TYPE_ALIAS = {
  flat: "Apartment",
  apartment: "Apartment",
  apartments: "Apartment",
  room: "Room",
  kotha: "Room",
  house: "House",
  home: "House",
  villa: "Villa",
  studio: "Studio",
  cottage: "Cottage",
  land: "Land",
  jagga: "Land",
  commercial: "Commercial",
  office: "Commercial",
  condo: "Condo",
  cabin: "Cabin",
};

function FindProperty() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const tr = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  const propertyTypes = [
    "House",
    "Apartment",
    "Villa",
    "Studio",
    "Cottage",
    "Land",
    "Commercial",
    "Condo",
    "Cabin",
    "Room",
  ];

  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilterdProperties] = useState([]);
  const [isPropertiesLoading, setIsPropertiesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [maxPrice, setMaxPrice] = useState(100000);
  const [selectedMinPrice, setSelectedMinPrice] = useState(0);
  const [selectedMaxPrice, setSelectedMaxPrice] = useState(100000);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1400);
  const [showSidebar, setShowSidebar] = useState(window.innerWidth > 1400 ? true : false);
  const [selectedBedrooms, setSelectdBedrooms] = useState("");
  const [selectedBathrooms, setSelectedBathrooms] = useState("");
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [selectedVerification, setSelectedVerification] = useState("");
  const [viewMode, setViewMode] = useState("list"); // "list" | "map"
  const [promoFrame, setPromoFrame] = useState(0);

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const getTypeLabel = (type) => {
    if (!type) return "";
    const normalized = String(type).trim().toLowerCase().replace(/\s+/g, " ");
    const key = normalized === "office space" ? "officeSpace" : normalized.replace(/\s+/g, "");
    const translated = t(`findProperty.categories.${key}`);
    return translated === `findProperty.categories.${key}` ? capitalize(normalized) : translated;
  };

  const getAmenityLabel = (amenity) => {
    if (!amenity) return amenity;
    const id = String(amenity).toLowerCase().replace(/\s/g, "");
    const translated = t(`amenities.${id}`);
    return translated === `amenities.${id}` ? amenity : translated;
  };

  const isPropertyVerified = (property) => {
    const verifiedFlag = property?.is_verified ?? property?.isVerified;
    const normalizedStatus = String(property?.verification_status || property?.verificationStatus || "").toLowerCase();

    if (verifiedFlag === true || verifiedFlag === 1 || verifiedFlag === "1" || verifiedFlag === "t") {
      return true;
    }
    return normalizedStatus === "approved" || normalizedStatus === "verified";
  };

  const handleCardClick = (propertyId, propertyData) => {
    // Store property data in sessionStorage for the popup to use
    sessionStorage.setItem(`property_${propertyId}`, JSON.stringify(propertyData));
    navigate(`/property/${propertyId}`);
  };

  const getTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case "house":
        return "#007bff"; // Blue
      case "apartment":
        return "#28a745"; // Green
      case "villa":
        return "#17a2b8"; // Cyan
      case "studio":
        return "#e83e8c"; // Pink
      case "cottage":
        return "#20c997"; // Teal
      case "land":
        return "#ffc107"; // Yellow
      case "commercial":
        return "#fd7e14"; // Orange
      case "condo":
        return "#6f42c1"; // Purple
      case "cabin":
        return "#8B4513"; // Brown
      case "room":
        return "#ff6b6b"; // Soft red
      default:
        return "#6c757d"; // Gray fallback
    }
  };

  const propertyList = Array.isArray(properties) ? properties : [];
  const visibleProperties = Array.isArray(filteredProperties)
    ? filteredProperties
    : [];
  const hasAnyProperties = propertyList.length > 0;

  const ownerPromoCopy = {
    empty: {
      eyebrow: [
        tr("findProperty.ownerPromo.empty.eyebrow1", "Trusted Owner Program"),
        tr("findProperty.ownerPromo.empty.eyebrow2", "Secure Listings, Zero Brokerage"),
        tr("findProperty.ownerPromo.empty.eyebrow3", "Owner First, Tenant Friendly"),
      ],
      headline: [
        tr("findProperty.ownerPromo.empty.headline1", "No broker fee. No listing charge. Start renting your home for free."),
        tr("findProperty.ownerPromo.empty.headline2", "Upload your property in minutes and rent out directly with zero middleman cost."),
        tr("findProperty.ownerPromo.empty.headline3", "Grow your rental income with free listing and secure owner controls."),
      ],
      body: [
        tr("findProperty.ownerPromo.empty.body1", "GharBhada gives homeowners a direct path to tenants with transparent pricing and secure messaging."),
        tr("findProperty.ownerPromo.empty.body2", "Create your owner account for free and publish listings without paying platform or broker fees."),
        tr("findProperty.ownerPromo.empty.body3", "You keep control of your listing, pricing, and conversations while we handle safe product workflows."),
      ],
      points: [
        tr("findProperty.ownerPromo.empty.point1", "100% free property listing"),
        tr("findProperty.ownerPromo.empty.point2", "No broker fee for owners or renters"),
        tr("findProperty.ownerPromo.empty.point3", "Free renting workflow from listing to inquiry"),
      ],
    },
    compact: {
      eyebrow: [
        tr("findProperty.ownerPromo.compact.eyebrow1", "Keep Growing Your Listings"),
        tr("findProperty.ownerPromo.compact.eyebrow2", "Trusted Owner Program"),
        tr("findProperty.ownerPromo.compact.eyebrow3", "Scale Rentals Without Brokerage"),
      ],
      headline: [
        tr("findProperty.ownerPromo.compact.headline1", "List more properties with 0 broker fee and 0 listing fee"),
        tr("findProperty.ownerPromo.compact.headline2", "Add your next home today and reach tenants faster with direct owner visibility."),
        tr("findProperty.ownerPromo.compact.headline3", "More properties, same promise: zero brokerage and free listing."),
      ],
      body: [
        tr("findProperty.ownerPromo.compact.body1", "Add another property and keep your rental funnel active with secure in-app communication."),
        tr("findProperty.ownerPromo.compact.body2", "Keep expanding with free listings while tenants discover trusted homes across Nepal."),
        tr("findProperty.ownerPromo.compact.body3", "List, manage, and respond in one place while keeping costs low for both sides."),
      ],
      points: [
        tr("findProperty.ownerPromo.compact.point1", "Free listing for every new property"),
        tr("findProperty.ownerPromo.compact.point2", "Direct owner-to-tenant communication"),
        tr("findProperty.ownerPromo.compact.point3", "No commission on successful renting"),
      ],
    },
  };

  const promoMode = hasAnyProperties ? "compact" : "empty";
  const promoCopy = ownerPromoCopy[promoMode];
  const promoShift = promoFrame % promoCopy.points.length;
  const promoPoints = promoCopy.points.map((_, index) => promoCopy.points[(index + promoShift) % promoCopy.points.length]);

  const uniqueAmenities = [
    ...new Set(propertyList.flatMap((p) => p.amenities || [])),
  ].sort();

  useEffect(() => {
    const handleResize = () => {
      const desktopViewport = window.innerWidth > 1400;
      setIsDesktop(desktopViewport);
      if (!desktopViewport) {
        setShowSidebar(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await fetch("/api/properties");
        const data = await response.json();
        const nextProperties = Array.isArray(data) ? data : [];
        setProperties(nextProperties);
        setFilterdProperties(nextProperties);
        const highestPrice = nextProperties.length
          ? Math.max(...nextProperties.map((p) => p.price))
          : 100000;
        setMaxPrice(highestPrice);
        setSelectedMinPrice(0);
        setSelectedMaxPrice(highestPrice);
      } catch (error) {
        console.error("Error fetching properties", error);
      } finally {
        setIsPropertiesLoading(false);
      }
    };
    fetchProperties();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.toString()) return;

    const cityParam = params.get("city") || params.get("q") || "";
    const typeParam = String(params.get("type") || "").trim().toLowerCase();
    const minPriceParam = Number(params.get("minPrice"));
    const maxPriceParam = Number(params.get("maxPrice"));
    const verifiedParam = String(params.get("verified") || "").trim().toLowerCase();

    if (cityParam) {
      setSearchTerm(cityParam);
    }

    if (typeParam) {
      const normalizedType = TYPE_ALIAS[typeParam] || "";
      if (normalizedType) {
        setSelectedType(normalizedType);
      }
    }

    if (Number.isFinite(minPriceParam) && minPriceParam >= 0) {
      setSelectedMinPrice(minPriceParam);
    }

    if (Number.isFinite(maxPriceParam) && maxPriceParam >= 0) {
      setSelectedMaxPrice(maxPriceParam);
    }

    if (verifiedParam === "true" || verifiedParam === "verified") {
      setSelectedVerification("verified");
    }
  }, [location.search]);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = properties.filter((p) => {
      const matchesSearch =
        (p.state && p.state.toLowerCase().includes(term)) ||
        (p.city && p.city.toLowerCase().includes(term)) ||
        (p.municipality && p.municipality.toLowerCase().includes(term));

      const matchesType = selectedType
        ? p.type.toLowerCase() === selectedType.toLowerCase()
        : true;

      const minP = selectedMinPrice === "" ? 0 : Number(selectedMinPrice);
      const maxP = selectedMaxPrice === "" || selectedMaxPrice === 0 ? maxPrice : Number(selectedMaxPrice);
      const matchesPrice = p.price >= minP && p.price <= maxP;

      const matchesBedrooms = selectedBedrooms
        ? p.bedrooms >= Number(selectedBedrooms)
        : true;

      const matchesBathrooms = selectedBathrooms
        ? p.bathrooms >= Number(selectedBathrooms)
        : true;

      const matchesAmenities = selectedAmenities.every((a) =>
        p.amenities.includes(a)
      );

      const matchesVerification =
        selectedVerification === ""
          ? true
          : selectedVerification === "verified"
          ? isPropertyVerified(p)
          : !isPropertyVerified(p);

      return (
        matchesSearch &&
        matchesType &&
        matchesPrice &&
        matchesBedrooms &&
        matchesBathrooms &&
        matchesAmenities &&
        matchesVerification
      );
    });
    setFilterdProperties(filtered);
  }, [
    searchTerm,
    selectedType,
    selectedMinPrice,
    selectedMaxPrice,
    selectedBedrooms,
    selectedBathrooms,
    selectedAmenities,
    selectedVerification,
    properties,
    maxPrice,
  ]);

  useEffect(() => {
    if (isPropertiesLoading) return undefined;
    const ticker = window.setInterval(() => {
      setPromoFrame((prev) => prev + 1);
    }, 3200);

    return () => window.clearInterval(ticker);
  }, [isPropertiesLoading]);

  return (
    <div className="container my-4">
      <div>
        <div className="d-flex flex-fill justify-content-center">
          <div className="search-bar input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder={t('findProperty.searchPlaceholder')}
              aria-label="Location Search"
              aria-describedby="basic-addon2"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => setSearchTerm(searchTerm)}
            >
              <FontAwesomeIcon icon={faSearch} />
            </button>
          </div>
        </div>
        <div className="search-selection d-flex flex-wrap justify-content-center mb-4">
          <button
            className="btn"
            onClick={() => {
              setFilterdProperties(properties);
              setSearchTerm("");
            }}
          >
            {t('findProperty.all')}
          </button>
          {propertyTypes.map((type) => (
            <button
              key={type}
              className="btn m-1"
              onClick={() => {
                const filtered = properties.filter(
                  (p) => p.type.toLowerCase() === type.toLowerCase()
                );
                setFilterdProperties(filtered);
                setSearchTerm("");
              }}
            >
              {getTypeLabel(type)}
            </button>
          ))}
        </div>
      </div>

      <div className="fp-toolbar mb-4">
        <div className="fp-toolbar-left">
          <div className="fp-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={`btn btn-sm ${viewMode === "list" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setViewMode("list")}
            >
              <FontAwesomeIcon icon={faList} className="me-1" />
              List
            </button>
            <button
              type="button"
              className={`btn btn-sm ${viewMode === "map" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setViewMode("map")}
            >
              <FontAwesomeIcon icon={faMap} className="me-1" />
              Map
            </button>
          </div>
          <div className="fp-results-pill">
            {visibleProperties.length} results
          </div>
        </div>

        <div className="fp-toolbar-right">
          <button
            className="btn btn-sm fp-filter-toggle"
            onClick={() => setShowSidebar(!showSidebar)}
          >
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            {showSidebar ? "Hide Filters" : "Show Filters"}
          </button>

          <button
            className="btn btn-sm fp-clear-all"
            onClick={() => {
              setSearchTerm("");
              setSelectedType("");
              setSelectedMinPrice(0);
              setSelectedMaxPrice(maxPrice);
              setSelectdBedrooms("");
              setSelectedBathrooms("");
              setSelectedAmenities([]);
              setSelectedVerification("");
              setFilterdProperties(properties);
            }}
          >
            Clear all
          </button>
        </div>
      </div>

      {!isPropertiesLoading && (
        <section
          className={`fp-owner-promo ${hasAnyProperties ? "fp-owner-promo--compact" : "fp-owner-promo--empty"}`}
          aria-label={tr("findProperty.ownerPromo.ariaLabel", "Owner listing promotion")}
        >
          <div className="fp-owner-promo__left">
            <span key={`promo-eyebrow-${promoFrame}`} className="fp-owner-promo__eyebrow fp-owner-promo__text-swap">
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              {promoCopy.eyebrow[promoFrame % promoCopy.eyebrow.length]}
            </span>
            <h3 key={`promo-headline-${promoFrame}`} className="fp-owner-promo__text-swap">
              {promoCopy.headline[promoFrame % promoCopy.headline.length]}
            </h3>
            <p key={`promo-body-${promoFrame}`} className="fp-owner-promo__text-swap fp-owner-promo__text-swap--delay">
              {promoCopy.body[promoFrame % promoCopy.body.length]}
            </p>

            <div className="fp-owner-promo__points">
              {promoPoints.map((point, idx) => (
                <span
                  key={`promo-point-${promoFrame}-${idx}`}
                  className="fp-owner-promo__text-swap"
                  style={{ animationDelay: `${idx * 0.12}s` }}
                >
                  <FontAwesomeIcon icon={faCheckCircle} /> {point}
                </span>
              ))}
            </div>
          </div>

          <div className="fp-owner-promo__right">
            <button
              className="btn fp-owner-promo__cta"
              onClick={() => navigate("/list-property")}
            >
              {tr("findProperty.ownerPromo.ctaPrimary", "List Property Free")}
              <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </button>
            <button
              className="btn fp-owner-promo__alt"
              onClick={() => navigate("/help")}
            >
              {tr("findProperty.ownerPromo.ctaSecondary", "How Free Listing Works")}
            </button>
          </div>
        </section>
      )}

      <div className="row">
        {showSidebar && (
          <>
            {!isDesktop && (
                <div
                    className="sidebar-overlay show"
                    onClick={()=>setShowSidebar(false)}
                ></div>
            )}
            <div className="col-12 col-xxl-3">
            <div className={`filter-sidebar ${showSidebar ? "show" : ""}`}>
              <h5 className="fw-semibold mb-3">
                <FontAwesomeIcon icon={faFilter} className="me-2" />
                {t('findProperty.filters')}
              </h5>

              <div className="filter-group mb-3">
                <label className="form-label fw-semibold">{t('findProperty.priceRange')}</label>
                <div className="price-range-inputs">
                  <div>
                    <label className="form-label small text-muted mb-1">Min (NPR)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      placeholder="e.g. 5000"
                      value={selectedMinPrice}
                      onChange={(e) => setSelectedMinPrice(e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)))}
                    />
                  </div>
                  <div>
                    <label className="form-label small text-muted mb-1">Max (NPR)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      placeholder={`e.g. ${maxPrice.toLocaleString()}`}
                      value={selectedMaxPrice}
                      onChange={(e) => setSelectedMaxPrice(e.target.value === "" ? maxPrice : Math.max(0, Number(e.target.value)))}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-between">
                  <small>NPR 0</small>
                  <small>NPR {maxPrice.toLocaleString()}</small>
                </div>
              </div>

              <div className="filter-group mb-3">
                <label className="form-label fw-semibold">{t('findProperty.propertyType')}</label>
                <select
                  className="form-select"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  <option value="">{t('findProperty.any')}</option>
                  {propertyTypes.map((type) => (
                    <option key={type} value={type}>
                      {getTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group mb-3">
                <label className="form-label fw-semibold">{tr("findProperty.verificationStatus", "Verification Status")}</label>
                <select
                  className="form-select"
                  value={selectedVerification}
                  onChange={(e) => setSelectedVerification(e.target.value)}
                >
                  <option value="">{t('findProperty.any')}</option>
                  <option value="verified">{tr("findProperty.verified", "Verified")}</option>
                  <option value="unverified">{tr("findProperty.unverified", "Unverified")}</option>
                </select>
              </div>

              <div className="filter-group mb-3">
                <div className="row">
                  <div className="col-6">
                    <label className="form-label fw-semibold">{t('findProperty.bedrooms')}</label>
                    <select
                      className="form-select"
                      value={selectedBedrooms}
                      onChange={(e) => setSelectdBedrooms(e.target.value)}
                    >
                      {[...new Set(propertyList.map((p) => p.bedrooms))]
                        .sort((a, b) => a - b)
                        .map((num) => (
                          <option key={num} value={num}>
                            {num}+
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold">{t('findProperty.bathrooms')}</label>
                    <select
                      className="form-select"
                      value={selectedBathrooms}
                      onChange={(e) => setSelectedBathrooms(e.target.value)}
                    >
                      {[...new Set(propertyList.map((p) => p.bathrooms))]
                        .sort((a, b) => a - b)
                        .map((num) => (
                          <option key={num} value={num}>
                            {num}+
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="filter-group mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label fw-semibold">{t('findProperty.amenities')}</label>
                  <button
                    type="button"
                    className="btn text-muted form-label py-0"
                    onClick={() => setShowAllAmenities(!showAllAmenities)}
                  >
                    {showAllAmenities ? (
                        <span>
                            {t('findProperty.showLess')} <FontAwesomeIcon icon={faCaretUp}/>
                        </span>
                    ):(
                        <span>
                            {t('findProperty.showMore')} <FontAwesomeIcon icon={faCaretDown}/>
                        </span>
                    )}
                  </button>
                </div>

                {uniqueAmenities
                  .slice(0, showAllAmenities ? uniqueAmenities.length : 5)
                  .map((amenity, index) => (
                    <div key={index} className="form-check">
                      <input
                        className="form-check-input mb-3"
                        type="checkbox"
                        checked={selectedAmenities.includes(amenity)}
                        id={`amenity-${index}`}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAmenities([
                              ...selectedAmenities,
                              amenity,
                            ]);
                          } else {
                            setSelectedAmenities(
                              selectedAmenities.filter((a) => a !== amenity)
                            );
                          }
                        }}
                      />
                      <label
                        className="form-check-label mb-2"
                        htmlFor={`amenity-${index}`}
                      >
                        {getAmenityLabel(amenity)}
                      </label>
                    </div>
                  ))}
              </div>

              <button 
                className="btn btn-primary w-100"
                onClick={()=>{
                    setSearchTerm("");
                    setSelectedType("");
                    setSelectedMinPrice(0);
                    setSelectedMaxPrice(maxPrice);
                    setSelectdBedrooms("");
                    setSelectedBathrooms("");
                    setSelectedAmenities([]);
                    setSelectedVerification("");
                    setFilterdProperties(properties);
                }}
            >
                {t('findProperty.clearFilters')}
            </button>
            </div>
            </div>
          </>
        )}

        <div className={isDesktop && showSidebar ? "col-12 col-xxl-9" : "col-12"}>
          {/* ── MAP VIEW ── */}
          {viewMode === "map" ? (
            <PropertyMapView properties={visibleProperties} />
          ) : (
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 justify-content-center">
            {isPropertiesLoading ? (
              Array.from({ length: 6 }).map((_, idx) => (
                <div className="col" key={`skeleton-${idx}`}>
                  <div className="card property-card shadow-sm border-0 h-100 skeleton-card">
                    <div className="property-skeleton-image skeleton-shimmer"></div>
                    <div className="card-body pb-0">
                      <div className="property-skeleton-line skeleton-shimmer mb-3"></div>
                      <div className="property-skeleton-line short skeleton-shimmer mb-3"></div>
                    </div>
                    <div className="px-3 mb-2">
                      <div className="property-skeleton-line short skeleton-shimmer mb-2"></div>
                      <div className="property-skeleton-line extra-short skeleton-shimmer"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : visibleProperties.length === 0 ? (
              <div
                className="col-12 d-flex justify-content-center align-items-center"
                style={{ minHeight: "240px" }}
              >
                <div className="text-center text-muted">
                  <FontAwesomeIcon icon={faSearch} size="2x" className="mb-2" />
                  <p>{t('findProperty.noProperties')}</p>
                  {hasAnyProperties && (
                    <button
                      className="btn fp-owner-inline-cta mt-2"
                      onClick={() => navigate("/list-property")}
                    >
                      {tr("findProperty.ownerPromo.inlineCta", "Own a home? List it free")}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              visibleProperties.map((property) => (
                <div
                  className="col"
                  key={property.id}
                  onClick={() => handleCardClick(property.id || property._id, property)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="card property-card shadow-sm border-0 h-100">
                    <div className="position-relative">
                      <img
                        src={
                          property.thumbnail
                            ? getPropertyImageUrl(property.thumbnail, { width: 520 })
                            : property.photos?.[0]
                            ? getPropertyImageUrl(property.photos[0], { width: 520 })
                            : ""
                        }
                        className="card-img-top"
                        alt={property.name}
                      />
                      <span
                        className="badge property-type-tag"
                        style={{
                          backgroundColor: getTypeColor(property.type),
                          color: "#fff",
                        }}
                      >
                        {getTypeLabel(property.type)}
                      </span>
                      <span
                        className={`badge rounded-pill property-verification-tag ${isPropertyVerified(property) ? "property-verification-tag--verified" : "property-verification-tag--unverified"}`}
                      >
                        <FontAwesomeIcon
                          icon={isPropertyVerified(property) ? faShieldAlt : faCircleXmark}
                          className="me-1"
                        />
                        {isPropertyVerified(property)
                          ? tr("findProperty.verified", "Verified")
                          : tr("findProperty.unverified", "Unverified")}
                      </span>
                    </div>

                    <div className="card-body pb-0">
                      <h5 className="card-title fw-bold fs-5">
                        {property.name}
                      </h5>
                      <p className="fs-6 text-muted mb-2">
                        {property.state}, {property.city}
                      </p>
                    </div>

                    <div className="px-3 mb-2">
                      <h4 className="property-price mb-0 fw-semibold">
                        NPR {property.price.toLocaleString()}
                      </h4>
                      <p className="fs-6 text-muted">{t('findProperty.perMonth')}</p>
                    </div>

                    <div className="d-flex justify-content-between text-center px-3 py-2 border-top border-bottm">
                      <div>
                        <span className="fs-6 fw-normal">
                          <strong>{property.bedrooms}</strong> {t('findProperty.bed')}
                        </span>
                      </div>
                      <div>
                        <span className="fs-6 fw-normal">
                          <strong>{property.bathrooms}</strong> {t('findProperty.bath')}
                        </span>
                      </div>
                      <div>
                        <span className="fs-6 fw-normal">
                          <strong>{property.sqft}</strong> {t('findProperty.sqft')}
                        </span>
                      </div>
                    </div>

                    <div className="px-3 py-2 text-muted small">
                      {property.amenities.map((a, i) => {
                        if (i < 3) {
                          return (
                            <span key={i} className="me-2 fs-6">
                              {a}
                            </span>
                          );
                        }
                        return null;
                      })}
                      {property.amenities.length > 3 && (
                        <span className="fs-6 fw-normal">
                          +{property.amenities.length - 3} more
                        </span>
                      )}
                    </div>

                    <div className="d-flex justify-content-between align-items-center px-3 pb-3">
                      <div className="rating text-warning">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <FontAwesomeIcon
                            key={i}
                            icon={
                              i < Math.round(property.rating)
                                ? faStar
                                : faStarRegular
                            }
                            className={
                              i < Math.round(property.rating)
                                ? "star-filled"
                                : "star-empty"
                            }
                          />
                        ))}
                        <span className="ms-1 text-muted small fs-6">
                          ({property.reviews})
                        </span>
                      </div>
                    </div>

                    <div className="contact-actions d-flex justify-content-end pb-2">
                      <button className="btn me-2 text-success">
                        <FontAwesomeIcon className="me-1" icon={faEnvelope} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          )} {/* end viewMode === "list" */}
        </div>
      </div>
    </div>
  );
}
export default FindProperty;
